import { Howl } from 'howler'
import { AudioPreview, AudioSectionContainer, PlayerStatus } from 'mytours-read-common'
import {
	audioLoaded,
	loadAudioError,
	setAudioEnded,
	setCurrentTime,
	setPlayerStatus,
} from 'mytours-read-common/src/audioplayer/actions'

import { getUAParser } from '../../services/uaParser'
import { resolveAudioSource } from './audioSource'
import { setCurrentSound } from './soundController'

// How often the playing position is pushed into the store. The audio player bar's seek bar is driven from
// it, so this is effectively the bar's frame rate; it matches the screen sections' own interval.
const POSITION_INTERVAL_MS = 500

// Narrower than react-redux's Dispatch so this module does not have to know the store's shape.
type DispatchAction = (action: unknown) => void

/**
 * A Howl for one media item, wired to the audio player store.
 *
 * The store updates are what put the track in the playlist and bring the audio player bar on screen with
 * it, so any player that creates its own sound should create it here rather than hand-rolling the
 * handlers — a player that skips `setPlayerStatus` plays audio with no visible transport anywhere.
 *
 * The caller is expected to have dispatched `loadAudioPreview` for this track first: the reducer only
 * updates playlist entries that already exist, so every status dispatch below is a no-op without it.
 */
export const createAudioSound = async (
	audio: AudioPreview,
	container: AudioSectionContainer,
	dispatch: DispatchAction,
): Promise<Howl | undefined> => {
	const source = await resolveAudioSource(audio)
	if (!source) {
		return undefined
	}

	const audioId = audio.id as number
	const { src, format, fileId } = source

	// firefox doesn't support html5 playing from howler
	// https://github.com/goldfire/howler.js/issues/1411
	const html5 = getUAParser().getBrowser().name !== 'Firefox'

	let positionInterval: ReturnType<typeof setInterval> | undefined
	const stopReportingPosition = (): void => {
		if (positionInterval !== undefined) {
			clearInterval(positionInterval)
			positionInterval = undefined
		}
	}

	const sound: Howl = new Howl({
		html5,
		format: [format],
		src,
		preload: 'metadata',
		onload: () => dispatch(audioLoaded({ audioId, duration: sound.duration(), container, fileId })),
		onloaderror: (_id, error: unknown) => dispatch(loadAudioError({ audioId, error, container, fileId })),
		onplay: () => {
			dispatch(setPlayerStatus({ audioId, status: PlayerStatus.Playing, container, fileId }))
			stopReportingPosition()
			positionInterval = setInterval(() => {
				dispatch(setCurrentTime({ audioId, time: sound.seek() as number, container, fileId }))
			}, POSITION_INTERVAL_MS)
		},
		onpause: () => {
			stopReportingPosition()
			dispatch(setPlayerStatus({ audioId, status: PlayerStatus.Pause, container, fileId }))
		},
		onstop: () => {
			stopReportingPosition()
			dispatch(setPlayerStatus({ audioId, status: PlayerStatus.Stop, container, fileId }))
			dispatch(setCurrentTime({ audioId, time: 0, container, fileId }))
		},
		onend: () => {
			stopReportingPosition()
			dispatch(setAudioEnded({ audioId, container, fileId }))
		},
	})

	setCurrentSound(audioId, container, fileId, sound)

	return sound
}
